import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
    BasicProviderProps,
    CatalogType,
    ResourceType,
    ResourceVersionArtifactType,
    ResourceVersionType,
    SoapModuleContextFormType,
    RolesResourceModuleContextType,
    PaginatorProps
} from '../types'
import ErrorBoundary from '../components/error/ErrorBoundary'
import { DashboardContext } from './DashboardContext';
import useDataClient from '../hooks/useDataClient';
import { AppContext } from './AppContext';
import { resourcesAdapter } from '../adapter';
import { useSearchParams } from 'react-router-dom';
import { PAGINATOR_OPTIONS, URLQUERY } from '../config';
import businessHelper from '../helpers/business.helper';
import matchHelper from '../helpers/match.helper';

export const RolesResourceModuleContext = createContext<RolesResourceModuleContextType | null>(null);

export default function RolesResourceModuleProvider({ children }: BasicProviderProps) {
    const appContext = useContext(AppContext)
    const dashboardContext = useContext(DashboardContext)
    const [searchParams] = useSearchParams();
    const newResourceClient = useDataClient<number>({ appContext })
    const editResourceClient = useDataClient<number>({ appContext })
    const updateVersionClient = useDataClient<number>({ appContext })
    const deleteResourceClient = useDataClient<number>({ appContext })
    const deleteArtifactClient = useDataClient<number>({ appContext })
    const rolesData = dashboardContext?.appData.roles
    const resourcesData = dashboardContext?.appData.linkedResources
    const [rolTarget, setRolTarget] = useState<number>(0)
    const [cardExpanded, setCardExpanded] = useState<number>(-1)
    const [resourceTarget, setResourceTarget] = useState<ResourceType | undefined>();
    const [showForm, setShowForm] = useState<SoapModuleContextFormType>(null)
    const [withPaginator, setWithPaginator] = useState<PaginatorProps>({ page: 0, perPage: PAGINATOR_OPTIONS[0] })
    const [search, setSearch] = useState('')

    const getMenuRolData = useCallback((idMenus: number[]) => businessHelper.getMenuRolData(idMenus, rolesData || []), [rolesData])

    const listData = useMemo<ResourceType[]>(() => {
        let data = (resourcesData || []).filter(item => !!getMenuRolData(item.linkedMenus || []).find(r => rolTarget !== 0 ? r.idRol === rolTarget : true)) || []
        return matchHelper.searchResources(data, search, matchHelper.matchParamsDefault, [])
    }, [resourcesData, rolTarget, search])

    const availableArtifactOptions = useMemo<CatalogType[]>(() => {
        let currentArtifacts = resourceTarget?.versions[0]?.artifacts.map(a => a.type) || []
        return (dashboardContext?.getCatalog('ART') || []).filter(c => c.active && !currentArtifacts.includes(String(c.value)))
    }, [dashboardContext, resourceTarget])

    const reloadResources = useCallback((message: string) => {
        dashboardContext?.updateAppData('linkedResources')
        setShowForm(null)
        appContext?.setAppDialog({ message: message, type: 'success' })
    }, [])

    function showResourceForm(resource?: ResourceType) {
        setShowForm('Referencia')
        setResourceTarget(resource)
    }

    function showArtifactForm(resource: ResourceType) {
        setShowForm('Artefacto')
        setResourceTarget(resource)
    }

    function addResource(request: ResourceType, files: File[]) {
        newResourceClient.executeRequest(resourcesAdapter.addResource(request, files))
    }

    function editResource(request: ResourceType) {
        editResourceClient.executeRequest(resourcesAdapter.editResource(request))
    }

    function editVersion(version: ResourceVersionType, files: File[]) {
        updateVersionClient.executeRequest(resourcesAdapter.updateVersion(version, files))
    }

    function updateArtifacts(version: ResourceVersionType, files: File[]) {
        updateVersionClient.executeRequest(resourcesAdapter.updateVersion(version, files))
    }

    function confirmResourceDelete(item: ResourceType) {
        appContext?.setAppDialog({
            title: 'Aviso',
            message: `¿Desea eliminar el recurso ${item.label}?`,
            type: 'warning',
            onAccept: () => deleteResourceClient.executeRequest(resourcesAdapter.deleteResource(item))
        })
    }

    function confirmArtifactDelete(data: ResourceVersionArtifactType) {
        appContext?.setAppDialog({
            title: 'Aviso',
            message: `¿Desea eliminar el artefacto ${data.name}?`,
            type: 'warning',
            onAccept: () => deleteArtifactClient.executeRequest(resourcesAdapter.deleteArtifact(data))
        })
    }

    const rolesModuleContext = useMemo<RolesResourceModuleContextType>(() => ({
        listData,
        availableArtifactOptions,
        rolTarget,
        rolesData: rolesData || [],
        cardExpanded,
        setCardExpanded,
        confirmResourceDelete,
        showForm,
        showResourceForm,
        showArtifactForm,
        closeForm: () => setShowForm(null),
        resourceTarget,
        search,
        setSearch,
        appPaginator: {
            total: listData.length,
            setWithPaginator: setWithPaginator,
            withPaginator: withPaginator
        },
        addResource,
        editResource,
        editVersion,
        updateArtifacts,
        confirmArtifactDelete,
        getMenuRolData
    }), [rolTarget, cardExpanded, listData, availableArtifactOptions, showForm, search, withPaginator])

    useEffect(() => {
        let requestAddedId = newResourceClient.status.data
        if (requestAddedId) {
            reloadResources(`Recurso agregado con id ${requestAddedId}`)
        }
    }, [newResourceClient.status.data])

    useEffect(() => {
        let requestAddedId = editResourceClient.status.data
        if (requestAddedId) {
            reloadResources(`Recurso editado`)
        }
    }, [editResourceClient.status.data])

    useEffect(() => {
        let requestUpdate = updateVersionClient.status.data
        if (requestUpdate) {
            reloadResources(`Artefactos actualizados`)
        }
    }, [updateVersionClient.status.data])

    useEffect(() => {
        if (deleteArtifactClient.status.data) {
            reloadResources('Artefacto eliminado')
        }
    }, [deleteArtifactClient.status.data])

    useEffect(() => {
        if (deleteResourceClient.status.data) {
            reloadResources('Recurso eliminado')
        }
    }, [deleteResourceClient.status.data])

    useEffect(() => {
        let target = searchParams.get(URLQUERY.group)
        let idCard = Number(searchParams.get(URLQUERY.target))
        let rolItem = dashboardContext?.appData.roles.find(r => r.id === Number(target))
        if (rolItem) {
            setRolTarget(rolItem.id)
            setWithPaginator({ ...withPaginator, page: 0 })
        }
        if (idCard) {
            setCardExpanded(idCard)
            setWithPaginator({ ...withPaginator, page: Math.trunc(listData.findIndex(c => c.id === idCard) / withPaginator.perPage) })
        }
    }, [searchParams, listData])

    useEffect(() => {
        setCardExpanded(-1)
    }, [search])

    useEffect(() => {
        if (!showForm) {
            setResourceTarget(undefined)
        }
    }, [showForm])

    return <ErrorBoundary>
        <RolesResourceModuleContext.Provider value={rolesModuleContext}>
            {children}
        </RolesResourceModuleContext.Provider>
    </ErrorBoundary>
}
